import React, { useState } from 'react';
import { Palette, Check } from 'lucide-react';

interface ColorPickerProps {
  value?: string;
  onChange: (color: string) => void;
  label: string;
  description?: string;
  placeholder?: string;
  className?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value = '',
  onChange,
  label,
  description,
  placeholder = '#000000',
  className = '',
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hexValue = e.target.value;
    // Validate hex format
    if (/^#[0-9A-Fa-f]{0,6}$/.test(hexValue) || hexValue === '') {
      onChange(hexValue);
    }
  };

  const handleHexInputBlur = () => {
    // Ensure the hex value is complete
    if (value && value.length === 4) {
      // Convert 3-digit hex to 6-digit
      const expanded = value.replace(/^#([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/, '#$1$1$2$2$3$3');
      onChange(expanded);
    } else if (value && value.length === 7 && /^#[0-9A-Fa-f]{6}$/.test(value)) {
      // Valid 6-digit hex, keep as is
    } else if (value && value !== '') {
      // Invalid hex, reset to empty
      onChange('');
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mb-2">{description}</p>
        )}
      </div>
      
      {/* Color Input Section */}
      <div className="flex items-center space-x-3">
        <div className="relative group">
          <input
            type="color"
            value={value || '#000000'}
            onChange={handleColorChange}
            className="w-12 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-gray-400 transition-colors"
            title="Click to open color picker"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Palette className="h-4 w-4 text-white drop-shadow-sm" />
          </div>
        </div>
        
        <div className="flex-1">
          <input
            type="text"
            value={value}
            onChange={handleHexInputChange}
            onBlur={handleHexInputBlur}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
            maxLength={7}
          />
        </div>
        
        {value && (
          <div className="relative">
            <div
              className="w-10 h-10 rounded-lg border-2 border-gray-300 shadow-sm"
              style={{ backgroundColor: value }}
              title={`Selected color: ${value}`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Check className="h-4 w-4 text-white drop-shadow-sm" />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ColorPicker;
